import React, { useState } from "react";
import { Box, Typography, Paper, Button, IconButton, Tooltip, Collapse, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Warning2 as Warning2IconIx } from "iconsax-react";
import { ApiIntegrationForm } from "../../../../../pages/Settings/ApiIntegrationPage";
import { EMPTY_API_ENTRY } from "../constants";
import { LabeledField, inboxInputSx } from "./LabeledField";

// Same neutral bordered style as every other button on this page
// (AgentConfigView.jsx's own neutralButtonSx) — duplicated rather than
// imported to avoid a circular import (AgentConfigView.jsx ->
// ActionConfigPanel.jsx -> this file). This one was still plain
// variant="outlined" (MUI green border/text by default), the one button
// on the Actions step still breaking the "never a green button" rule
// (Rahim, 2026-09-03, screenshot).
const neutralButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 600,
};

export default function ApiIntegrationPanel({ cfg, updateConfig }) {
  const entries = Array.isArray(cfg?.entries) ? cfg.entries : [];
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const addEntry = () => {
    const entry = EMPTY_API_ENTRY();
    updateConfig("api_integration", "entries", [...entries, entry]);
    setExpandedIds((prev) => new Set([...prev, entry.id]));
  };

  const updateEntry = (id, patch) => {
    updateConfig(
      "api_integration",
      "entries",
      entries.map((e) => (e.id === id ? { ...e, ...patch } : e))
    );
  };

  const removeEntry = (id) => {
    updateConfig("api_integration", "entries", entries.filter((e) => e.id !== id));
    setExpandedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Moved flex-end -> flex-start (Rahim, 2026-09-03: "firly position
          it to the left side"). */}
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addEntry} sx={neutralButtonSx}>
          Add API Integration
        </Button>
      </Box>

      {/* Same "!" in a circle + bold text.secondary treatment as the
          other Actions-step hint paragraphs (Rahim, 2026-09-03: "u didnt
          add for this"). */}
      {entries.length === 0 && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <Box sx={{ width: 16, height: 16, flexShrink: 0, mt: "1px", display: "flex", color: "text.secondary" }}>
            <Warning2IconIx size="100%" variant="Bold" />
          </Box>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "text.secondary", lineHeight: 1.5 }}>
            No API integrations added yet. Each integration can have its own trigger condition.
          </Typography>
        </Box>
      )}

      {entries.map((entry, i) => (
        <Paper key={entry.id} variant="outlined" sx={{ overflow: "hidden", borderRadius: "8px" }}>
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2, py: 1.25, cursor: "pointer",
              bgcolor: "action.hover", "&:hover": { bgcolor: "action.selected" },
            }}
            onClick={() => toggleExpand(entry.id)}
          >
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary", flex: 1 }}>
              API Integration #{i + 1}
              {entry.whenToRun && (
                <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", ml: 1, fontWeight: 400 }}>
                  — {entry.whenToRun.length > 50 ? entry.whenToRun.slice(0, 50) + "…" : entry.whenToRun}
                </Typography>
              )}
              {entry.apiUrl && !entry.whenToRun && (
                <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", ml: 1, fontWeight: 400 }}>
                  {entry.apiUrl.length > 50 ? entry.apiUrl.slice(0, 50) + "…" : entry.apiUrl}
                </Typography>
              )}
            </Typography>
            <Tooltip title="Delete this API integration">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeEntry(entry.id); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {expandedIds.has(entry.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>

          <Collapse in={expandedIds.has(entry.id)} unmountOnExit>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <LabeledField label="When to run">
                <TextField
                  size="small" fullWidth multiline minRows={2}
                  placeholder="Describe when this API integration should be triggered…"
                  value={entry.whenToRun || ""}
                  onChange={(e) => updateEntry(entry.id, { whenToRun: e.target.value })}
                  sx={inboxInputSx}
                />
              </LabeledField>
              <ApiIntegrationForm
                value={{
                  apiUrl: entry.apiUrl || "",
                  method: entry.method || "POST",
                  headers: entry.headers || {},
                  formConfig: entry.formConfig || {},
                }}
                onChange={(newVal) => updateEntry(entry.id, newVal)}
                showOrderFields={false}
              />
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Box>
  );
}
